/**
 * Form draft auto-save.
 *
 * Saves the form's current state to localStorage every 5 seconds while the
 * user is editing. Restores on form re-open if the form was abandoned.
 *
 * Keys: `draft:expense:{statementId}:{lineItemId|new}` so each form (statement
 * + line item) gets its own slot. Cleared explicitly on successful submit.
 *
 * The hook returns the restored values (or null) on first call, plus a
 * `save` function for the form to call on every input change. We debounce
 * inside `save` so we don't write on every keystroke.
 *
 * Why localStorage and not sessionStorage:
 *   sessionStorage is cleared when the user closes the tab. localStorage
 *   survives reboots, which matters for users who get interrupted (most
 *   users). Privacy: localStorage is scoped to origin, so only the app
 *   reads it.
 *
 * Cleanup: drafts older than 14 days are purged on initial app load. See
 * `purgeOldDrafts()` below.
 */

import { useCallback, useEffect, useRef } from 'react';

const DRAFT_PREFIX = 'draft:';
const DRAFT_DEBOUNCE_MS = 5_000;
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

interface StoredDraft<T> {
  value: T;
  savedAt: number; // ms-since-epoch
}

/** Build a deterministic localStorage key for a draft. */
function key(kind: string, statementId: string, lineItemId: string | null): string {
  return `${DRAFT_PREFIX}${kind}:${statementId}:${lineItemId ?? 'new'}`;
}

/**
 * Read a previously saved draft. Returns null if missing, expired, or unparseable.
 */
export function readDraft<T>(
  kind: string,
  statementId: string,
  lineItemId: string | null = null,
): T | null {
  try {
    const raw = localStorage.getItem(key(kind, statementId, lineItemId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft<T>;
    if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(key(kind, statementId, lineItemId));
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

/**
 * Hook that returns a debounced save function for a form. Wires up an
 * effect that flushes pending writes on unmount, so a draft is persisted
 * even if the user closes the modal mid-debounce.
 */
export function useDraftAutosave<T>(
  kind: string,
  statementId: string,
  lineItemId: string | null = null,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<T | null>(null);

  const flush = useCallback(() => {
    if (pendingRef.current === null) return;
    try {
      const stored: StoredDraft<T> = {
        value: pendingRef.current,
        savedAt: Date.now(),
      };
      localStorage.setItem(
        key(kind, statementId, lineItemId),
        JSON.stringify(stored),
      );
    } catch {
      // localStorage may throw on Safari private mode or quota exceeded.
      // Best-effort: silently drop the draft, since it's a UX nicety.
    }
    pendingRef.current = null;
  }, [kind, statementId, lineItemId]);

  const save = useCallback(
    (value: T) => {
      pendingRef.current = value;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, DRAFT_DEBOUNCE_MS);
    },
    [flush],
  );

  // Flush on unmount so half-typed drafts don't get lost.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      flush();
    };
  }, [flush]);

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingRef.current = null;
    try {
      localStorage.removeItem(key(kind, statementId, lineItemId));
    } catch {
      // ignore
    }
  }, [kind, statementId, lineItemId]);

  return { save, clear };
}

/**
 * Purge expired drafts on app load. Call once from main.tsx.
 *
 * We don't bother with delete-by-prefix since localStorage is small and
 * iterating all keys is fast. No risk of removing non-draft keys because
 * we filter by the DRAFT_PREFIX.
 */
export function purgeOldDrafts(): void {
  try {
    const now = Date.now();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(DRAFT_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw) as StoredDraft<unknown>;
        if (now - parsed.savedAt > DRAFT_TTL_MS) keysToRemove.push(k);
      } catch {
        // Unparseable — remove it; can only be cruft.
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
